import { useRouteContext } from '@/app/layouts/TenantLayout'
import { type NavItem, getProjectSettingsNav, organizationSettingsNavData } from '@/data/nav-data'
import { useNavScope } from '@/hooks/useNavScope'
import { type NavRouteTarget, getNavRouteTarget } from '@/lib/navRoutes'
import { billingPath, isOrganizationLevelRoute, orgPath, projectPath } from '@/lib/routeScope'
import { type PlatformUser, selectShowTabs } from '@/stores/auth'
import { useUser } from '@/stores/authStore'
import { getConsoleUrl, isConsoleDeployment } from '@/utils/deployment'
import { useIsAdmin, useIsOwner } from '@/utils/permissions'
import { useLocation } from '@tanstack/react-router'
import { Activity, CreditCard, type LucideIcon, Settings } from 'lucide-react'
import { useMemo } from 'react'
import type { ArrangeableItem } from './footerBarModel'

export interface FooterItem extends ArrangeableItem {
	title: string
	icon: LucideIcon
	isSettingsTab: boolean
	target: NavRouteTarget | { href: string }
}

const TABS_SHOWN_BY_DEFAULT = new Set([
	'streams-settings',
	'users-settings',
	'org-streams-settings',
	'org-users-settings',
])

/** The settings item already opens General. */
const TABS_LEFT_OUT = new Set(['general-settings', 'org-general-settings'])

/** Everything the bar can hold on this page, in default order. */
export function useFooterItems(): FooterItem[] {
	const { tenantId, orgId } = useRouteContext()
	const { pathname } = useLocation()
	const navScope = useNavScope()
	const user = useUser() as PlatformUser | null
	const isAdmin = useIsAdmin()
	const isOwner = useIsOwner()
	const projectTenantId = isOrganizationLevelRoute(pathname) ? undefined : tenantId
	const isOrgContext = !projectTenantId

	return useMemo(() => {
		const items: FooterItem[] = []
		if (!orgId) {
			return items
		}
		const hasSettings = !isConsoleDeployment()
		if (hasSettings) {
			const settingsTo = projectTenantId
				? projectPath(orgId, projectTenantId, isAdmin ? '/settings/general' : '/settings/metadata')
				: orgPath(orgId, '/settings/general')
			items.push({
				id: 'settings',
				title: isOrgContext ? 'Organization settings' : 'Project settings',
				icon: Settings,
				isSettingsTab: false,
				shownByDefault: true,
				target: { to: settingsTo, params: undefined },
			})
		}
		if (isOrgContext) {
			// TODO: needs discussion — move monitoring URL to an environment variable instead of deriving from host
			items.push({
				id: 'monitoring',
				title: 'Monitoring',
				icon: Activity,
				isSettingsTab: false,
				shownByDefault: true,
				target: { href: `https://monitor.${window.location.host}/` },
			})
		}
		if (isOrgContext && isOwner) {
			items.push({
				id: 'billing',
				title: 'Billing',
				icon: CreditCard,
				isSettingsTab: false,
				shownByDefault: true,
				target: { href: `${getConsoleUrl()}${billingPath(orgId)}` },
			})
		}
		if (hasSettings) {
			const tabs: NavItem[] = isOrgContext
				? organizationSettingsNavData
				: getProjectSettingsNav(selectShowTabs(user), user, isAdmin)
			for (const tab of tabs.filter(tab => !TABS_LEFT_OUT.has(tab.id))) {
				items.push({
					id: tab.id,
					title: tab.title,
					icon: tab.icon ?? Settings,
					isSettingsTab: true,
					shownByDefault: TABS_SHOWN_BY_DEFAULT.has(tab.id),
					target: getNavRouteTarget(tab.path, navScope),
				})
			}
		}
		return items
	}, [orgId, projectTenantId, isOrgContext, isAdmin, isOwner, user, navScope])
}
